import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useNotification } from "./NotificationContext";

const SocketContext = createContext(null);

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const { user, token, logout, updateToken } = useAuth();
  const { showNotification } = useNotification();
  
  // Referencias para evitar recreaciones innecesarias
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const userRef = useRef(user);
  const tokenRef = useRef(token);
  const mountedRef = useRef(true);
  
  // Actualizar referencias sin causar re-renders
  userRef.current = user;
  tokenRef.current = token;

  // Función estable para crear conexión
  const createSocketConnection = useCallback(() => {
    if (!userRef.current || !tokenRef.current) {
      console.log('No hay usuario o token, evitando conexión socket');
      
      // Mostrar notificación específica por falta de credenciales
      showNotification({
        message: "No se puede conectar al sistema: credenciales no disponibles",
        severity: "error",
        autoHideDuration: 4000,
      });
      
      return null;
    }

    if (isConnectingRef.current) {
      console.log('Conexión ya en progreso, evitando duplicado');
      return socketRef.current;
    }

    if (socketRef.current?.connected) {
      console.log('Socket ya conectado, reutilizando conexión existente');
      return socketRef.current;
    }

    isConnectingRef.current = true;
    console.log('Creando nueva conexión socket');

    const socketUrl = process.env.REACT_APP_API_URL_IO_PRODUCTION;//process.env.REACT_APP_API_URL_IO
    
    const newSocket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: false,
      auth: {
        token: tokenRef.current,
      },
      query: {
        userId: userRef.current.id,
        userEmail: userRef.current.email,
        userName: `${userRef.current.first_name.trim()} ${userRef.current.first_last_name.trim()} ${
          userRef.current.second_last_name || ''
        }`.trim(),
        userRole: userRef.current.role_name,
        documentNumber: userRef.current.document_number,
        clientVersion: '1.0.0',
        connectionTime: Date.now(),
        isWhatsAppClient: 'true'
      },
    });

    // Configurar eventos una sola vez
    const setupSocketEvents = () => {
      newSocket.on('connect', () => {
        if (!mountedRef.current) return;
        
        console.log('Socket conectado:', newSocket.id);
        isConnectingRef.current = false;
        socketRef.current = newSocket;
        setSocket(newSocket);
        
        // Verificación defensiva antes de acceder a propiedades del usuario
        if (userRef.current && userRef.current.first_name) {
          showNotification({
            message: `${userRef.current.first_name.trim()} se ha conectado al sistema`,
            severity: 'success',
            autoHideDuration: 4000,
          });
        } else {
          showNotification({
            message: "Conexión establecida al sistema",
            severity: 'success',
            autoHideDuration: 4000,
          });
        }
      });

      newSocket.on("token_refresh", ({ token: newToken }) => {
        console.log("Token renovado automáticamente");
        updateToken(newToken);
        tokenRef.current = newToken;
      });

      newSocket.on("authentication_error", (error) => {
        console.error("Error de autenticación:", error);
        showNotification({
          message: "Sesión expirada. Por favor, inicie sesión nuevamente.",
          severity: "error",
          autoHideDuration: 6000,
        });
        logout();
      });

      newSocket.on("disconnect", (reason) => {
        if (!mountedRef.current) return;
        
        console.log("Socket desconectado:", reason);
        isConnectingRef.current = false;
        
        // Solo limpiar el socket si el componente está montado
        if (socketRef.current === newSocket) {
          socketRef.current = null;
          setSocket(null);
        }

        if (reason === "io server disconnect") {
          showNotification({
            message: "Sesión expirada o inválida",
            severity: "error",
            autoHideDuration: 4000,
          });
          logout();
        } else if (reason !== "io client disconnect") {
          // Solo mostrar notificación si no fue desconexión intencional
          // Verificación defensiva antes de acceder a propiedades del usuario
          const userName = userRef.current?.first_name?.trim() || 'Usuario';
          
          showNotification({
            message: `${userName} se ha desconectado del sistema`,
            severity: "warning",
            autoHideDuration: 4000,
          });
        }
      });

      newSocket.on("connect_error", (error) => {
        console.error("Error de conexión:", error);
        isConnectingRef.current = false;

        if (
          error.message.includes("Autenticación inválida") ||
          error.message.includes("token") ||
          error.message.includes("Token expirado")
        ) {
          showNotification({
            message: "Sesión expirada. Por favor, inicie sesión nuevamente.",
            severity: "error",
            autoHideDuration: 4000,
          });
          logout();
        } else {
          showNotification({
            message: "Error al conectar con el servidor de tiempo real",
            severity: "error",
            autoHideDuration: 4000,
          });
        }
      });
    };

    setupSocketEvents();
    return newSocket;
  }, []); // Sin dependencias para mantener la función estable

  // Efecto principal para manejar la conexión
  useEffect(() => {
    mountedRef.current = true;

    if (user && token) {
      const connectionTimeout = setTimeout(() => {
        if (!mountedRef.current) return;
        
        const newSocket = createSocketConnection();
        if (!newSocket) return;

        // Configurar timeout de respuesta inicial
        const responseTimeout = setTimeout(() => {
          if (newSocket && !newSocket.connected) {
            console.log('Timeout de conexión, intentando polling como fallback');
            newSocket.io.opts.transports = ['polling', 'websocket'];
          }
        }, 5000);

        return () => clearTimeout(responseTimeout);
      }, 100); // Pequeño delay para evitar conexiones inmediatas múltiples

      return () => {
        clearTimeout(connectionTimeout);
      };
    } else {
      // Limpiar socket si no hay usuario/token
      if (socketRef.current) {
        console.log('Limpiando socket por falta de credenciales');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    }

    return () => {
      mountedRef.current = false;
    };
  }, [user?.id, token, createSocketConnection]); // Dependencias mínimas y estables

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      isConnectingRef.current = false;
      
      if (socketRef.current) {
        console.log('Desconectando socket al desmontar componente');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};